// ============================================================
// TMR Portal Admin — status de negócio da Empresa: o badge principal e a troca
// ============================================================
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn, StatusBadge } from '../../components/primitives';
import { Icons, StrokeIcon } from '../../lib/icons';
import type { CompanyBusinessStatus } from '../../data/companies/contract';
import { BUSINESS_STATUS_BADGE, BUSINESS_STATUS_OPTIONS } from '../../data/companies/status';
import { useChangeCompanyStatus } from '../../data/companies/queries';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link CompanyStatusControl}. */
interface CompanyStatusControlProps {
  /** The Empresa whose status this is. */
  companyId: string;
  /** The status in force right now. */
  status: CompanyBusinessStatus;
}

/**
 * The Empresa's business status as the detail screen's leading badge, and the way to move it (10 B6a).
 *
 * The badge is the same one the list shows; what this adds is the move. Until M3 there are no automatic
 * transitions — the Contracts module will drive contract → signature → completed — so without a control here
 * every Empresa would sit at "Pendente contrato" forever and the badge would be decoration.
 *
 * Without `company.update` it renders as a plain badge: the server refuses the request either way, and the
 * UI only hides what the operator cannot do.
 */
export function CompanyStatusControl({ companyId, status }: CompanyStatusControlProps) {
  const [picking, setPicking] = useState(false);
  const canUpdate = usePermission(PERMISSIONS.companyUpdate);

  if (!canUpdate) return <StatusBadge status={BUSINESS_STATUS_BADGE[status]} />;

  return (
    <>
      <button
        type="button"
        onClick={() => setPicking(true)}
        title="Alterar status da empresa"
        style={{
          background: 'transparent',
          border: 0,
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          color: 'var(--fg-muted)',
        }}
      >
        <StatusBadge status={BUSINESS_STATUS_BADGE[status]} />
        <StrokeIcon d={Icons.pen} size={13} />
      </button>

      {picking && (
        <StatusPicker companyId={companyId} status={status} onClose={() => setPicking(false)} />
      )}
    </>
  );
}

/** Props for {@link StatusPicker}. */
interface StatusPickerProps {
  companyId: string;
  status: CompanyBusinessStatus;
  onClose: () => void;
}

/**
 * The statuses as rows, in lifecycle order.
 *
 * Every one is offered whatever the current status is: the backend enforces no state machine yet, on purpose
 * (the real order arrives with the contract flow in M3), and a wrong move is corrected by moving again. The
 * dialog closes only once the request has succeeded — a lifecycle move that silently failed would leave the
 * operator believing the carteira says something it does not.
 *
 * "Inativa" asks first. It is the only status whose effect leaves the portal — the Empresa disappears from
 * the legacy system and stops being able to take contracts — and one click on a list of otherwise
 * interchangeable rows is too little for that.
 */
function StatusPicker({ companyId, status, onClose }: StatusPickerProps) {
  const change = useChangeCompanyStatus(companyId);
  const [confirming, setConfirming] = useState<CompanyBusinessStatus | null>(null);
  const stop = (e: MouseEvent) => e.stopPropagation();

  const move = (next: CompanyBusinessStatus) => {
    if (next === status) return onClose();
    if (next === 'Inactive') return setConfirming(next);
    change.mutate(next, { onSuccess: onClose });
  };

  if (confirming) {
    return (
      <div
        onClick={change.isPending ? undefined : onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      >
        <div
          onClick={stop}
          style={{ width: 420, maxWidth: '100%', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}
        >
          <div style={{ padding: '22px 24px 6px' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 17, margin: 0, letterSpacing: '-0.01em' }}>
              Inativar esta empresa?
            </h3>
            <p style={{ fontSize: 13.5, color: 'var(--fg-muted)', margin: '8px 0 0', lineHeight: 1.55 }}>
              Ela sai das listagens do sistema legado e deixa de emitir contratos. Para voltar, basta
              escolher outro status.
            </p>
          </div>

          {change.isError && (
            <p role="alert" style={{ margin: '12px 24px 0', fontSize: 12.5, color: 'oklch(38% 0.12 28)' }}>
              {change.error instanceof ApiError ? change.error.detail : 'Não foi possível alterar o status agora.'}
            </p>
          )}

          <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn variant="secondary" onClick={() => setConfirming(null)} disabled={change.isPending}>
              Voltar
            </Btn>
            <Btn onClick={() => change.mutate(confirming, { onSuccess: onClose })} disabled={change.isPending}>
              {change.isPending ? 'Inativando…' : 'Inativar'}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <OverlayPortal>
      <div
        onClick={change.isPending ? undefined : onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      >
        <div
          onClick={stop}
          style={{ width: 420, maxWidth: '100%', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}
        >
          <div style={{ padding: '22px 24px 6px' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 17, margin: 0, letterSpacing: '-0.01em' }}>
              Status da empresa
            </h3>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', margin: '6px 0 0', lineHeight: 1.5 }}>
              Onde a empresa está no ciclo de vida do contrato.
            </p>
          </div>

          <div style={{ padding: '14px 24px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {BUSINESS_STATUS_OPTIONS.map((option) => {
              const active = option.value === status;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={change.isPending}
                  onClick={() => move(option.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '10px 13px',
                    textAlign: 'left',
                    cursor: change.isPending ? 'default' : 'pointer',
                    border: `1px solid ${active ? 'var(--forest-900)' : 'var(--border-default)'}`,
                    background: active ? 'var(--sage-100)' : 'var(--cream-50)',
                    borderRadius: 'var(--radius-sm)',
                    opacity: change.isPending && !active ? 0.6 : 1,
                  }}
                >
                  <StatusBadge status={BUSINESS_STATUS_BADGE[option.value]} />
                  {active && (
                    <span style={{ color: 'var(--forest-800)', display: 'inline-flex' }}>
                      <StrokeIcon d={Icons.check} size={16} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {change.isError && (
            <p role="alert" style={{ margin: '12px 24px 0', fontSize: 12.5, color: 'oklch(38% 0.12 28)' }}>
              {change.error instanceof ApiError ? change.error.detail : 'Não foi possível alterar o status agora.'}
            </p>
          )}

          <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={onClose} disabled={change.isPending}>
              {change.isPending ? 'Salvando…' : 'Fechar'}
            </Btn>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
